import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Clock, XCircle } from 'lucide-react';
import BRAND from '../../config/branding';
import EnhancedSignatureCapture from '../../components/documents/EnhancedSignatureCapture';
import { filterClientFacingSections, parseStructuredContent } from '../../../../shared/contractPreview';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const HEADER_LOGO = '/branding/stratel-logo-light.png';

function renderSectionBlocks(section) {
  const blocks = parseStructuredContent(section.content, section.key, { docType: 'spo' });
  return blocks.map((block, index) => {
    if (block.type === 'table') {
      return (
        <div key={`${section.key}-table-${index}`} className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {block.headers.map((header) => (
                  <th key={header} className="px-3 py-2 text-left font-semibold border-b border-gray-200">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`${section.key}-row-${rowIndex}`} className="border-b border-gray-100 last:border-b-0">
                  {block.headers.map((header, cellIndex) => (
                    <td key={`${header}-${rowIndex}`} className="px-3 py-2 align-top text-gray-700 whitespace-pre-wrap">
                      {row[cellIndex] || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div key={`${section.key}-text-${index}`} className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
        {block.content}
      </div>
    );
  });
}

export default function PlacementOrderPublicPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const previewQuery = searchParams.get('preview') === '1' ? '?preview=1' : '';

  const [data, setData] = useState(null);
  const [issuerProfile, setIssuerProfile] = useState({ issuerName: '', issuerEmail: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responding, setResponding] = useState(false);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [showSignForm, setShowSignForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [signerName, setSignerName] = useState('');
  const [openSections, setOpenSections] = useState({});

  useEffect(() => {
    fetch(`${API_BASE}/placement-orders/public/${token}${previewQuery}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || 'Failed to load Staffing Placement Order.');
        return d;
      })
      .then((d) => {
        setData({
          ...d.order,
          sections: filterClientFacingSections(d.sections || []),
        });
        setSignerName(d.order?.recipientName || '');
        setIssuerProfile(d.issuerProfile || { issuerName: '', issuerEmail: '' });
        if (Array.isArray(d.sections) && d.sections.length) {
          setOpenSections({ [d.sections[0].key]: true });
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, previewQuery]);

  const toggleSection = (key) => setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const respond = async (action, options = {}) => {
    if (action === 'sign' && !showSignForm) {
      setShowSignForm(true);
      return;
    }
    if (action === 'decline' && !showDeclineForm) {
      setShowDeclineForm(true);
      return;
    }

    setResponding(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE}/placement-orders/public/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          signerName: options.signerName ?? signerName.trim(),
          signatureData: options.signatureData,
          metadata: options.metadata,
          reason: declineReason.trim(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || 'Unable to process response.');
      setData((prev) => ({ ...prev, status: action === 'sign' ? 'signed' : 'declined' }));
      setShowDeclineForm(false);
      setShowSignForm(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-700" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
          <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Link Unavailable</h2>
          <p className="text-gray-500">{error}</p>
          <p className="text-sm text-gray-400 mt-4">
            Please contact <strong>{issuerProfile.issuerName || BRAND.name}</strong> to request a new link.
          </p>
        </div>
      </div>
    );
  }

  const order = data;
  const isTerminal = ['signed', 'declined', 'expired', 'cancelled'].includes(order.status);

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="relative rounded-t-2xl overflow-hidden text-white">
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(90deg, #d9fffb 0%, #8df0e3 40%, #0f766e 100%)' }}
          />
          <div className="relative px-8 py-5 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <img
                src={BRAND.logo}
                alt={`${BRAND.name} logo`}
                className="h-32 w-auto object-contain drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)] flex-shrink-0"
                onError={(e) => { e.currentTarget.src = HEADER_LOGO; }}
              />
              <div>
                <div className="text-5xl font-bold tracking-tight leading-none">{issuerProfile.issuerName || BRAND.name}</div>
                <div className="text-sm font-semibold mt-3 uppercase tracking-[0.18em] opacity-80">Healthcare Staffing</div>
                <div className="text-2xl font-semibold mt-3 text-teal-900">Staffing Placement Order</div>
              </div>
            </div>
            <div className="text-right text-2xl font-bold text-teal-950">{order.orderNumber}</div>
          </div>
        </div>

        <div className="bg-white rounded-b-2xl shadow-xl p-8">
          {order.status === 'signed' && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-xl px-5 py-4 mb-8">
              <CheckCircle size={20} />
              <span className="font-semibold">You have signed this Staffing Placement Order.</span>
            </div>
          )}
          {order.status === 'declined' && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 rounded-xl px-5 py-4 mb-8">
              <XCircle size={20} />
              <span className="font-semibold">You have declined this Staffing Placement Order.</span>
            </div>
          )}
          {order.status === 'expired' && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-5 py-4 mb-8">
              <Clock size={20} />
              <span className="font-semibold">This Staffing Placement Order has expired.</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm mb-8">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400 font-semibold">Prepared For</div>
              <div className="text-2xl font-semibold text-gray-900 mt-2">{order.recipientName || order.organizationId?.name || 'Client Organization'}</div>
              <div className="text-gray-500 mt-1">{order.organizationId?.name || ''}</div>
              <div className="text-gray-500">{order.recipientEmail || ''}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-gray-400 font-semibold">Prepared By</div>
              <div className="text-2xl font-semibold text-gray-900 mt-2">{issuerProfile.issuerName || BRAND.name}</div>
              <div className="text-gray-500 mt-1">{issuerProfile.issuerEmail || ''}</div>
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Staffing Placement Order - {order.organizationId?.name || order.facilityName || 'Client Organization'}
          </h1>

          <div className="space-y-4">
            {(order.sections || []).map((section) => {
              const open = openSections[section.key];
              return (
                <div key={section.key} className="rounded-2xl border border-gray-200 overflow-hidden bg-white">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.key)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 text-left"
                  >
                    <span className="font-semibold text-gray-800">{section.label}</span>
                    {open ? <ChevronUp size={18} className="text-gray-500" /> : <ChevronDown size={18} className="text-gray-500" />}
                  </button>
                  {open && (
                    <div className="px-5 py-5 space-y-4">
                      {renderSectionBlocks(section)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {!isTerminal && (
            <div className="mt-10 pt-8 border-t">
              <p className="text-sm text-gray-400 text-center mb-4">
                Please review the staffing placement order above and indicate your response below.
              </p>

              {error && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  {error}
                </div>
              )}

              {!showSignForm && !showDeclineForm && (
                <div className="flex justify-center gap-4">
                  <button
                    type="button"
                    disabled={responding}
                    onClick={() => respond('sign')}
                    className="px-8 py-4 rounded-2xl font-semibold text-white bg-teal-700 hover:bg-teal-600 disabled:opacity-50"
                  >
                    Sign Staffing Placement Order
                  </button>
                  <button
                    type="button"
                    disabled={responding}
                    onClick={() => respond('decline')}
                    className="px-8 py-4 rounded-2xl font-semibold text-red-400 border border-red-200 bg-white hover:bg-red-50 disabled:opacity-50"
                  >
                    Decline
                  </button>
                </div>
              )}

              {showSignForm && (
                <div className="mt-4 p-5 rounded-2xl border border-teal-200 bg-teal-50/50">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Signer Name</label>
                  <input
                    type="text"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm mb-4"
                    placeholder="Enter your full name"
                  />
                  <EnhancedSignatureCapture
                    signerName={signerName}
                    signerEmail={order.recipientEmail || ''}
                    onSignatureComplete={(signatureData, metadata) =>
                      respond('sign', {
                        signerName,
                        signatureData,
                        metadata,
                      })}
                    disabled={responding || !signerName.trim()}
                    title="Signature"
                    subtitle="Draw your signature to approve this staffing placement order."
                    submitLabel={responding ? 'Signing...' : 'Apply Signature'}
                    requiresMultipleSigners={false}
                  />
                </div>
              )}

              {showDeclineForm && (
                <div className="mt-4 p-5 rounded-2xl border border-red-200 bg-red-50/60">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Decline</label>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    className="w-full rounded-xl border border-gray-300 px-4 py-3 text-sm min-h-[120px]"
                    placeholder="Let Stratel know why this placement order is being declined..."
                  />
                  <div className="mt-4 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowDeclineForm(false)}
                      className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={responding}
                      onClick={() => respond('decline')}
                      className="px-4 py-2 rounded-lg bg-red-600 text-white disabled:opacity-50"
                    >
                      {responding ? 'Submitting...' : 'Submit Decline'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
